import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Settings, Send, ShieldCheck, Palette, Image as ImageIcon, X, Upload, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useAppConfig } from "@/lib/AppConfigContext";

const empty = {
  smtp_host: "", smtp_port: 587, smtp_username: "",
  use_tls: true, use_ssl: false, from_email: "", from_name: "", company_name: "",
  primary_color: "#002FA7", text_color: "#0D0E15", bg_color: "#FFFFFF",
  logo_base64: "", app_logo_base64: "",
  request_delivery_receipt: false, request_read_receipt: false,
  smtp_password: "",
};

export default function SmtpSettings() {
  const [form, setForm] = useState(empty);
  const [hasPassword, setHasPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const { refresh: refreshAppConfig } = useAppConfig();

  useEffect(() => {
    api.getSmtp().then((c) => {
      if (c) {
        setForm({ ...empty, ...c, smtp_password: "" });
        setHasPassword(!!c.has_password);
      }
    });
  }, []);

  const onSave = async () => {
    if (!hasPassword && !form.smtp_password) {
      toast.error("Ingresá la contraseña SMTP");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, smtp_port: parseInt(form.smtp_port) };
      if (!form.smtp_password) delete payload.smtp_password;
      await api.saveSmtp(payload);
      toast.success("Configuración guardada");
      setForm({ ...form, smtp_password: "" });
      setHasPassword(true);
      refreshAppConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!testTo) { toast.error("Ingresá un email destino"); return; }
    setTesting(true);
    try {
      const r = await api.testSmtp({ to_email: testTo });
      toast.success(r.message || "Email enviado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en envío");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <div className="mb-6 md:mb-8">
        <p className="text-[10px] text-[#5A5D6E] uppercase tracking-[0.2em] font-medium">Sistema</p>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mt-2">Configuración</h1>
        <p className="text-sm text-[#5A5D6E] mt-2">SMTP, marca y apariencia del PDF/email enviado a proveedores.</p>
      </div>

      <div className="bg-white border border-[#E2E4E9] p-4 md:p-6 rounded-sm">
        <div className="flex items-center gap-2 mb-6">
          <Settings className="w-4 h-4 text-[#5A5D6E]" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Servidor</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Host SMTP *</Label>
            <Input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-input-host" />
          </div>
          <div>
            <Label>Puerto *</Label>
            <Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} data-testid="smtp-input-port" />
          </div>
          <div>
            <Label>Usuario *</Label>
            <Input value={form.smtp_username} onChange={(e) => setForm({ ...form, smtp_username: e.target.value })} placeholder="usuario@dominio.com" data-testid="smtp-input-username" />
          </div>
          <div>
            <Label>Contraseña {hasPassword && <span className="text-xs text-[#5A5D6E] normal-case">(dejar vacío para mantener)</span>}</Label>
            <Input type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} placeholder={hasPassword ? "••••••••" : "Contraseña / App password"} data-testid="smtp-input-password" />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 mt-4">
          <div className="flex items-center gap-2">
            <Switch checked={form.use_tls} onCheckedChange={(v) => setForm({ ...form, use_tls: v, use_ssl: v ? false : form.use_ssl })} data-testid="smtp-tls-switch" />
            <Label className="cursor-pointer">STARTTLS (puerto 587)</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.use_ssl} onCheckedChange={(v) => setForm({ ...form, use_ssl: v, use_tls: v ? false : form.use_tls })} data-testid="smtp-ssl-switch" />
            <Label className="cursor-pointer">SSL/TLS directo (puerto 465)</Label>
          </div>
        </div>

        <div className="border-t border-[#E2E4E9] my-6"></div>

        <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium mb-4">Remitente</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Email remitente *</Label>
            <Input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} placeholder="pedidos@miempresa.com" data-testid="smtp-input-from-email" />
          </div>
          <div>
            <Label>Nombre remitente</Label>
            <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder="Compras Mi Empresa" data-testid="smtp-input-from-name" />
          </div>
          <div className="sm:col-span-2">
            <Label>Nombre empresa (aparece en pie del PDF)</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Mi Empresa S.A." data-testid="smtp-input-company" />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button onClick={onSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002FA7]/90 rounded-sm w-full sm:w-auto" data-testid="smtp-save-button">
            <ShieldCheck className="w-4 h-4 mr-2" /> {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E4E9] p-4 md:p-6 rounded-sm mt-6">
        <div className="flex items-center gap-2 mb-6">
          <Palette className="w-4 h-4 text-[#5A5D6E]" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Branding (PDF y email)</p>
        </div>

        <BrandingSection form={form} setForm={setForm} />

        <div className="flex justify-end mt-6">
          <Button onClick={onSave} disabled={saving} variant="outline" className="rounded-sm w-full sm:w-auto" data-testid="branding-save-button">
            <ShieldCheck className="w-4 h-4 mr-2" /> Guardar branding
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E4E9] p-4 md:p-6 rounded-sm mt-6">
        <div className="flex items-center gap-2 mb-6">
          <ImageIcon className="w-4 h-4 text-[#5A5D6E]" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Logo de la app</p>
        </div>
        <p className="text-sm text-[#5A5D6E] mb-4">Reemplaza el ícono azul que aparece en el menú lateral y en la barra superior móvil.</p>
        <AppLogoSection form={form} setForm={setForm} />
        <div className="flex justify-end mt-6">
          <Button onClick={onSave} disabled={saving} variant="outline" className="rounded-sm w-full sm:w-auto" data-testid="app-logo-save-button">
            <ShieldCheck className="w-4 h-4 mr-2" /> Guardar logo de la app
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E4E9] p-4 md:p-6 rounded-sm mt-6">
        <div className="flex items-center gap-2 mb-4">
          <BellRing className="w-4 h-4 text-[#5A5D6E]" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium">Confirmaciones de email</p>
        </div>
        <p className="text-sm text-[#5A5D6E] mb-4">
          Solicita al proveedor confirmación de entrega y/o lectura. Las respuestas llegan a tu email remitente.
          <br />
          <span className="text-xs">⚠️ Son <strong>solicitudes no vinculantes</strong>: depende del cliente de email del destinatario (Outlook empresarial las soporta, Gmail personal puede ignorarlas).</span>
        </p>
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <Switch
              checked={form.request_delivery_receipt}
              onCheckedChange={(v) => setForm({ ...form, request_delivery_receipt: v })}
              data-testid="smtp-delivery-receipt-switch"
            />
            <div>
              <Label className="cursor-pointer">Solicitar confirmación de entrega</Label>
              <p className="text-xs text-[#5A5D6E]">El servidor del proveedor avisa si el email se entregó (o rebotó).</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Switch
              checked={form.request_read_receipt}
              onCheckedChange={(v) => setForm({ ...form, request_read_receipt: v })}
              data-testid="smtp-read-receipt-switch"
            />
            <div>
              <Label className="cursor-pointer">Solicitar confirmación de lectura</Label>
              <p className="text-xs text-[#5A5D6E]">El cliente de email del destinatario le pregunta si quiere confirmar que abrió el mensaje.</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <Button onClick={onSave} disabled={saving} variant="outline" className="rounded-sm w-full sm:w-auto" data-testid="receipts-save-button">
            <ShieldCheck className="w-4 h-4 mr-2" /> Guardar confirmaciones
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E4E9] p-4 md:p-6 rounded-sm mt-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A5D6E] font-medium mb-4">Probar configuración</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            placeholder="email@destino.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="rounded-sm"
            data-testid="smtp-test-email-input"
          />
          <Button onClick={onTest} disabled={testing} variant="outline" className="rounded-sm whitespace-nowrap" data-testid="smtp-test-button">
            <Send className="w-4 h-4 mr-2" /> {testing ? "Enviando…" : "Enviar prueba"}
          </Button>
        </div>
        <p className="text-xs text-[#5A5D6E] mt-2">Enviá un email de prueba con un pedido ficticio para validar tu SMTP.</p>
      </div>
    </div>
  );
}

function AppLogoSection({ form, setForm }) {
  const fileRef = useRef(null);

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("La imagen debe pesar menos de 500 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, app_logo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 border border-dashed border-[#E2E4E9] rounded-sm flex items-center justify-center bg-[#F7F7F9] overflow-hidden shrink-0">
        {form.app_logo_base64 ? (
          <img src={form.app_logo_base64} alt="logo app" className="max-h-full max-w-full object-contain" data-testid="app-logo-preview" />
        ) : (
          <div className="w-12 h-12 bg-[#002FA7] flex items-center justify-center rounded-sm">
            <Send className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onPick} className="hidden" data-testid="app-logo-input" />
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-sm" data-testid="app-logo-upload">
          <Upload className="w-3.5 h-3.5 mr-1" /> {form.app_logo_base64 ? "Cambiar logo" : "Subir logo"}
        </Button>
        {form.app_logo_base64 && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, app_logo_base64: "" })} className="rounded-sm text-[#D32F2F]" data-testid="app-logo-remove">
            <X className="w-3.5 h-3.5 mr-1" /> Quitar logo
          </Button>
        )}
        <p className="text-xs text-[#5A5D6E]">PNG/JPG/SVG, máx. 500 KB. Cuadrado preferido.</p>
      </div>
    </div>
  );
}

function BrandingSection({ form, setForm }) {
  const fileRef = useRef(null);

  const onPickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("La imagen debe pesar menos de 500 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm({ ...form, logo_base64: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => setForm({ ...form, logo_base64: "" });

  return (
    <div className="space-y-6">
      <div>
        <Label className="flex items-center gap-2"><ImageIcon className="w-3.5 h-3.5" /> Logo</Label>
        <div className="mt-2 flex items-center gap-4">
          <div className="w-32 h-20 border border-dashed border-[#E2E4E9] rounded-sm flex items-center justify-center bg-[#F7F7F9] overflow-hidden">
            {form.logo_base64 ? (
              <img src={form.logo_base64} alt="logo" className="max-h-full max-w-full object-contain" data-testid="branding-logo-preview" />
            ) : (
              <ImageIcon className="w-8 h-8 text-[#E2E4E9]" strokeWidth={1.25} />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onPickLogo} className="hidden" data-testid="branding-logo-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-sm" data-testid="branding-logo-upload">
              <Upload className="w-3.5 h-3.5 mr-1" /> {form.logo_base64 ? "Cambiar logo" : "Subir logo"}
            </Button>
            {form.logo_base64 && (
              <Button type="button" variant="ghost" size="sm" onClick={removeLogo} className="rounded-sm text-[#D32F2F]" data-testid="branding-logo-remove">
                <X className="w-3.5 h-3.5 mr-1" /> Quitar logo
              </Button>
            )}
            <p className="text-xs text-[#5A5D6E]">PNG/JPG/SVG, máx. 500 KB</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ColorField label="Color principal" testid="branding-primary-color" value={form.primary_color} onChange={(v) => setForm({ ...form, primary_color: v })} hint="Encabezados PDF/email" />
        <ColorField label="Color de texto" testid="branding-text-color" value={form.text_color} onChange={(v) => setForm({ ...form, text_color: v })} hint="Texto principal del PDF" />
        <ColorField label="Color de fondo" testid="branding-bg-color" value={form.bg_color} onChange={(v) => setForm({ ...form, bg_color: v })} hint="Fondo de PDF (futuro)" />
      </div>

      <div className="border border-[#E2E4E9] rounded-sm p-4" style={{ background: form.bg_color || "#FFFFFF" }} data-testid="branding-preview">
        <div className="flex items-center gap-3 mb-3">
          {form.logo_base64 && <img src={form.logo_base64} alt="" className="h-10 object-contain" />}
          <span className="font-display text-2xl font-bold" style={{ color: form.primary_color }}>{form.company_name || "Mi Empresa"}</span>
        </div>
        <div className="border-b-2 mb-2" style={{ borderColor: form.primary_color }}></div>
        <p className="text-sm" style={{ color: form.text_color }}>Vista previa del encabezado del PDF / email.</p>
        <table className="w-full text-xs mt-3" style={{ color: form.text_color }}>
          <thead>
            <tr style={{ background: form.primary_color, color: "#fff" }}>
              <th className="p-2 text-left">Referencia</th>
              <th className="p-2 text-left">Descripción</th>
              <th className="p-2">Cant.</th>
            </tr>
          </thead>
          <tbody>
            <tr><td className="p-2 border-t border-[#E2E4E9]">REF-001</td><td className="p-2 border-t border-[#E2E4E9]">Producto ejemplo</td><td className="p-2 border-t border-[#E2E4E9] text-center">10</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid, hint }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-sm border border-[#E2E4E9] cursor-pointer"
          data-testid={`${testid}-picker`}
        />
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="rounded-sm font-mono text-xs uppercase"
          data-testid={testid}
        />
      </div>
      {hint && <p className="text-[10px] text-[#5A5D6E] mt-1">{hint}</p>}
    </div>
  );
}
